import { useEffect, useRef, useState, useCallback, Dispatch, SetStateAction, useMemo } from 'react';
//import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { CellEditingStoppedEvent, ColDef, IRowNode, RowClassParams, SelectionChangedEvent } from 'ag-grid-community';
import { AG_GRID_LOCALE_RU } from 'blick.react.aggrid';
import { Button, ButtonGroup } from 'react-bootstrap'
import AddDeviceForm from './forms/AddDeviceForm'
import AccountInfo from './AccountInfo'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import SetDevicePropertyForm from './forms/SetDevicePropertyForm';
import { FindDeviceButton, FindDeviceInput } from './forms/FindDeviceForm';
import CellEditor from './CheckBoxEditor';
import context from './context/shared';
import { centerClient } from '../../shared/clients';
import type { RemoveDevicesModel, SetBannedModel, SetBlockedModel, SetBalanceModel, SetDealerCostModel, SetChargeModel, SetTestModeModel, SetImeiModel, SetCommentModel, SetStoragePeriodModel, CommandResult, Device, Account, SuccessModelOfCommandResult } from '../../shared/generated';
import { confirm } from '../../utils/confirm';
import { buildDeviceId } from '../../utils/deviceId';
import { dateComparator } from '../variables';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { PencilSquare } from 'react-bootstrap-icons';
import { EditCommentDialog } from './EditCommentDialog';
import { AccountMessage, AccountMessageDialog } from './AccountMessageDialog';
//import { myAgReactTheme } from '../../utils/AgGrid/agGridTheme';
import { useIsMobile } from '../shared';
import MobileDevicesList from '../mobile/MobileDevicesList';
import { agTextColumnFilterProps, agNumberColumnFilterProps, BlickAgGrid, DateFilter, SelectFilter } from 'blick.react.aggrid';
import { AgGridReact } from 'ag-grid-react';
import { BooleanFilter } from 'blick.react.aggrid/dist/agGridTheme';

ModuleRegistry.registerModules([AllCommunityModule]);

export interface IDeviceColMeta {
    defaultValue?: string | number | boolean;
    required?: boolean;
    needForCreate?: boolean;
}

export interface IDevicesColDef extends ColDef<Device> {
    /** Метаданные формы создания устройства (AG Grid: кастомные поля colDef запрещены) */
    context?: IDeviceColMeta;
}

/* --- helper functions unchanged (getDefaultColumnWidth, getMaxColumnWidth, getMinColumnWidth, getRowStyle) --- */
const getDefaultColumnWidth = (field: keyof (Device)) => {
    switch (field) {
        case 'Balance':
            return 75;
        case 'Blocked':
            return 75;
        case 'Banned':
            return 75;
        case 'Cost':
            return 70;
        case 'Charge':
            return 100;
        case 'Serial':
            return 80;
        case 'LastDataTime':
            return 150;
        case 'DeviceModelName':
            return 150;
        case 'Imei':
            return 150;
        case 'TestMode':
            return 100;
        case 'Comment':
            return 500;
        case 'StoragePeriod':
            return 75;
        case 'TodayCount':
            return 65;
        default:
            return 150;
    }
}
const getMaxColumnWidth = (field: keyof (Device)) => {
    switch (field) {
        case 'Balance':
            return 75;
        case 'Blocked':
            return 75;
        case 'Banned':
            return 75;
        case 'Cost':
            return 100;
        case 'Charge':
            return 100;
        case 'Serial':
            return 100;
        case 'LastDataTime':
            return 175;
        case 'DeviceModelName':
            return 200;
        case 'Imei':
            return 150;
        case 'Comment':
            return 700;
        case 'TestMode':
            return 150;
        case 'StoragePeriod':
            return 100;
        case 'TodayCount':
            return 75;
        default:
            return 500;
    }
}
const getMinColumnWidth = (field: keyof(Device)) => {
    switch (field) {
        case 'Comment':
            return 150;
        case 'DeviceModelName':
            return 100;
        default:
            return getDefaultColumnWidth(field);
    }
}
const getRowStyle = (row: RowClassParams) => {
    const banned = row.data.Banned;
    const blocked = row.data.Blocked;
    const selected = row.node.isSelected();
    if (!banned && !blocked && !selected)
        return;

    return {
        selectedHover: selected ? "var(--selected-row-hover)" : "",
        afterColor: selected ? "var(--selected-row)" : "",
        backgroundColor: banned ? "var(--banned)" : (blocked ? "var(--blocked)" : "var(--bg)"),
        background: banned ? "var(--banned)" : (blocked ? "var(--blocked)" : "var(--bg)"),
        color: selected || banned || blocked ? "var(--text-dark)" : 'var(--text)',
        stroke: selected || banned || blocked ? "var(--text-dark)" : 'var(--text)',
    }
}
export interface ICommentToEdit {
    deviceId: string;
    comment: string;
}
interface IDevicesTableProps {
    data: Device[];
    account: Account;
    setAccount: Dispatch<SetStateAction<Account>>;
    isLoading: boolean;
}
const numberCellEditorParams = {
    cellEditor: 'agNumberCellEditor',
    cellEditorParams: {
        min: 0
    }
}
const DevicesTable = (props: IDevicesTableProps) => {
    const { data, account, setAccount, isLoading } = props;
    const { refreshTree, foundDeviceToShow, setFoundDeviceToShow } = context;
    const [accountMessage, setAccountMessage] = useState<AccountMessage | null>(null);
    const [showCommentDialog, setShowCommentDialog] = useState<boolean>(false);
    const [commentToEdit, setCommentToEdit] = useState<ICommentToEdit | null>(null);
    const [selectedDevices, setSelectedDevices] = useState<Device[]>([]);
    const [showMessageDialog, setShowMessageDialog] = useState<boolean>(false);

    const isMobile = useIsMobile(768);

    const getStoredColumnWidth = (field: keyof (Device)) => {
        const widthsStorage = getWidthStorage();
        if (widthsStorage) {
            const columnWidth = widthsStorage[field];
            if (columnWidth)
                return columnWidth;
        }
        return getDefaultColumnWidth(field);
    }
    const ShowCommentDialogButton = ({deviceId, comment }) => {
        const handleClick = () => {
            setShowCommentDialog(true);
            setCommentToEdit({
                deviceId: deviceId,
                comment: comment
            })
        }
        return (
            <Button style={{ width: '2rem', padding: '0', background:'transparent' }} onClick={handleClick} variant='custom'>            
                <PencilSquare></PencilSquare>
            </Button>
        )
    }
    function getWidthStorage() {
        const devicesTableWidthsLs = localStorage.getItem("devicesTableWidths");
        return devicesTableWidthsLs ? JSON.parse(devicesTableWidthsLs) : {};
    }

    const resizeHandle = (state) => {
        if (!state.column)
            return;
        const colId = state.column.colId;
        const colWidth = state.column.actualWidth;
        let widthsStorage = getWidthStorage();
        if (!widthsStorage)
            widthsStorage = {};

        widthsStorage[colId] = colWidth;
        localStorage.setItem("devicesTableWidths", JSON.stringify(widthsStorage));
    }

    const columns = useMemo<IDevicesColDef[]>(() =>
        [
            {
                headerName: "№",
                editable: false,
                context: { needForCreate: false },
                valueGetter: "node.rowIndex + 1",
                resizable: false,
                width: 50,
                maxWidth:50
            },
            {
                field: 'DeviceModelName',
                headerName: 'Модель',
                ...SelectFilter,
                editable: false,
                context: { needForCreate: false, required: true },
                suppressSizeToFit: true,
                //floatingFilter: true,
                width: getStoredColumnWidth('DeviceModelName'),
                maxWidth: getMaxColumnWidth('DeviceModelName'),
                minWidth: getDefaultColumnWidth('DeviceModelName'),
            },
            {
                field: 'Banned',
                headerName: "Откл.",
                editable: true,
                ...BooleanFilter,
                cellRenderer: (row) => {
                    const value = row.data?.Banned;
                    return value ? "Да" : "Нет";
                },
                filterValueGetter: params => params.data?.Banned ? 1 : 0,
                cellEditor: CellEditor,
                width: getStoredColumnWidth('Banned'),
                maxWidth: getMaxColumnWidth('Banned'),
                minWidth: getDefaultColumnWidth('Banned'),
                context: { needForCreate: false, required: false },
            },
            {
                field: 'Serial',
                headerName: "с/н",
                ...agTextColumnFilterProps(),
                editable: false,
                context: { needForCreate: false },
                //floatingFilter: true,
                width: getStoredColumnWidth('Serial'),
                maxWidth: getMaxColumnWidth('Serial'),
                minWidth: getDefaultColumnWidth('Serial')
            },
            {
                field: 'Balance',
                headerName: 'Баланс',
                suppressSizeToFit: true,
                ...agNumberColumnFilterProps(),
                ...numberCellEditorParams,
                context: { defaultValue: 0, required: true, needForCreate: true },
                width: getStoredColumnWidth('Balance'),
                maxWidth: getMaxColumnWidth('Balance'),
                minWidth: getDefaultColumnWidth('Balance'),
                editable: true
            },
            {
                field: 'Blocked',
                headerName: "Забл.",
                ...BooleanFilter,
                editable: true,
                cellEditor: CellEditor,
                context: { needForCreate: false },
                cellRenderer: (row) => {
                    const value = row.data?.Blocked;
                    return value ? "Да" : "Нет";
                },
                filterValueGetter: params => params.data?.Blocked ? 1 : 0,
                width: getStoredColumnWidth('Blocked'),
                maxWidth: getMaxColumnWidth('Blocked'),
                minWidth: getDefaultColumnWidth('Blocked'),
                ...BooleanFilter,
            },
            {
                field: 'TodayCount',
                headerName: 'Подкл.',
                context: { needForCreate: false, defaultValue: 0 },
                suppressSizeToFit: true,
                ...agNumberColumnFilterProps(),
                ...numberCellEditorParams,
                width: getStoredColumnWidth('TodayCount'),
                maxWidth: getMaxColumnWidth('TodayCount'),
                minWidth: getDefaultColumnWidth('TodayCount'),
                editable: false
            },
            {
                field: 'LastDataTime',
                headerName: "Посл. данные",
                editable: false,
                context: { needForCreate: false },
                ...DateFilter,
                comparator: dateComparator,
                cellRenderer: (row) => {
                    if (!row.value)
                        return '';
                    return row.value.toLocaleString();
                },
                valueGetter: (row) => {
                    return row.data?.LastDataTime?.Seconds ? new Date(row.data?.LastDataTime.Seconds * 1000) : null;
                },
                width: getStoredColumnWidth('LastDataTime'),
                maxWidth: getMaxColumnWidth('LastDataTime'),
                minWidth: getDefaultColumnWidth('LastDataTime')
            },
            {
                field: 'Cost',
                headerName: 'Ц.Д.',
                ...agNumberColumnFilterProps(),
                editable: true,
                ...numberCellEditorParams,
                context: { needForCreate: true, defaultValue: 55 },
                width: getStoredColumnWidth('Cost'),
                maxWidth: getMaxColumnWidth('Cost'),
                minWidth: getDefaultColumnWidth('Cost')
            },
            {
                field: 'Charge',
                headerName: 'Аб. плата',
                ...agNumberColumnFilterProps(),
                ...numberCellEditorParams,
                editable: true,
                context: { defaultValue: 1, needForCreate: true, required: true },
                width: getStoredColumnWidth('Charge'),
                maxWidth: getMaxColumnWidth('Charge'),
                minWidth: getDefaultColumnWidth('Charge')
            },
            {
                field: 'StoragePeriod',
                headerName: 'Пер. хран.',
                ...agNumberColumnFilterProps(),
                editable: true,
                ...numberCellEditorParams,
                cellRenderer: (row) => {
                    const value = row.data[row.colDef.field];
                    return value;
                },
                context: { needForCreate: true, defaultValue: 90, required: true },
                width: getStoredColumnWidth('StoragePeriod'),
                maxWidth: getMaxColumnWidth('StoragePeriod'),
                minWidth: getDefaultColumnWidth('StoragePeriod')
            },
            {
                field: 'Imei',
                headerName: 'IMEI',
                ...agNumberColumnFilterProps(),
                context: { defaultValue: '' },
                editable: true,
                onCellValueChanged: (e) => {
                    if (e.newValue.length > 15)
                        toast.info("IMEI был сокращен, т.к. максимальная длина IMEI - 15 символов.")
                    e.data.Imei = e.newValue.toString().slice(0, 15)
                },
                width: getStoredColumnWidth('Imei'),
                maxWidth: getMaxColumnWidth('Imei'),
                minWidth: getDefaultColumnWidth('Imei')
            },
            {
                field: 'TestMode',
                headerName: 'Тест',
                ...agTextColumnFilterProps(),
                context: { needForCreate: true, defaultValue: false },
                editable: false,
                width: getStoredColumnWidth('TestMode'),
                maxWidth: getMaxColumnWidth('TestMode'),
                minWidth: getDefaultColumnWidth('TestMode')
            },
            {
                field: 'Comment',
                headerName: 'Комментарий',
                ...agTextColumnFilterProps(),
                context: { needForCreate: false, defaultValue: '' },
                editable: false,
                width: getStoredColumnWidth('Comment'),
                maxWidth: getMaxColumnWidth('Comment'),
                minWidth: getMinColumnWidth('Comment'),
                cellRenderer: (row) => {
                    const value = row.data[row.colDef.field];
                    const deviceId = buildDeviceId(row.data.Model, row.data.Serial);

                    return (
                        <div onDoubleClick={() => setShowCommentDialog(true)} title={value} style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ width: '90%', textOverflow: 'ellipsis', overflow: 'hidden' }}>{value}</span>
                            <ShowCommentDialogButton deviceId={deviceId} comment={value} />
                        </div>
                    )
                }
            }
        ],[]);

    // AG Grid #2: getRowId = Model + Serial; отбрасываем дубликаты, приходящие с сервера
    const uniqueDevices = useMemo(() => {
        const seen = new Set<string>();
        return data.filter(d => {
            const id = buildDeviceId(d.Model, d.Serial);
            if (seen.has(id))
                return false;
            seen.add(id);
            return true;
        });
    }, [data]);

    const sizeToFit = useCallback(() => {
        // AG Grid #29: не вызываем sizeColumnsToFit, если контейнер ещё не виден (нулевая ширина)
        const el = tableContainerRef.current;
        if (!el || el.clientWidth === 0)
            return;
        table.current?.api?.sizeColumnsToFit({
            defaultMinWidth: 100
        });
    }, []);

    useEffect(() => {
        window.addEventListener("resize", sizeToFit)
        return () => window.removeEventListener("resize", sizeToFit)
    }, [sizeToFit]);


    //const rowVirtualizerInstanceRef = useRef(null);
    //const [sorting, setSorting] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    useEffect(() => {
        checkFoundDevices();
    }, [data]);

    useEffect(() => {
        if (data.length > 0) sizeToFit();
    }, []);

    const handleRemoveDevices = async () => {
        if (selectedDevices.length == 0)
           return toast.info("Необходимо выбрать хотя бы один контроллер");

        const serialNumbers: string[] = [];
        let deviceNames = "";
        selectedDevices.map((row) => {
            const deviceToRemove = row;
            const deviceId = buildDeviceId(deviceToRemove.Model, deviceToRemove.Serial);
            const deviceName = deviceToRemove.DeviceModelName + " " + deviceToRemove.Serial;
            serialNumbers.push(deviceId);
            deviceNames += deviceName + "\r\n";
        })
        if (selectedDevices.length > 10)
            deviceNames = "Всего приборов - " + selectedDevices.length + ".";

        const confirmRemove = confirm({
            confirmation: `Вы действительно хотите удалить устройства? Устройства будут удалены вместе с данными.`,
            options: {
                body: deviceNames
            }
        });
        if (await confirmRemove)       
        {
            removeDevices(serialNumbers);
        }
    };

    const removeDevices = (serialNumbers) => {
        const sendBody: RemoveDevicesModel = {
            ClientId: account.ClientId,
            DeviceIds: serialNumbers
        };
        centerClient.removeDevices(sendBody)
            .then(() => successRemoveDevices())
            .catch(() => void 0);
    };

    const successRemoveDevices = () => {
        refreshTree();
        toast.success("Устройства успешно удалены");
    }

    const successSetProp = (res?: CommandResult) => {
        if (res?.Success) {
            toast.success("Свойство изменено!");
            refreshTree();
        }
        else {
            refreshTree();
            toast.error(res?.Description);
        }
    }
    function handleChangeProperty(deviceId: string, prop: keyof (Device), val) {
        let promise: Promise<SuccessModelOfCommandResult> | null = null;
        switch (prop) {
            case 'Banned': {
                const model: SetBannedModel = { DeviceIds: [deviceId], Banned: val };
                promise = centerClient.setBanned(model);
                break;
            }
            case 'Blocked': {
                const model: SetBlockedModel = { DeviceIds: [deviceId], Blocked: val };
                promise = centerClient.setBlocked(model);
                break;
            }
            case 'Balance': {
                const model: SetBalanceModel = { DeviceIds: [deviceId], Balance: val };
                promise = centerClient.setBalance(model);
                break;
            }
            case 'Cost': {
                const model: SetDealerCostModel = { DeviceIds: [deviceId], Cost: val };
                promise = centerClient.setCost(model);
                break;
            }
            case 'Charge': {
                const model: SetChargeModel = { DeviceIds: [deviceId], Charge: val };
                promise = centerClient.setCharge(model);
                break;
            }
            case 'TestMode': {
                const model: SetTestModeModel = { DeviceIds: [deviceId] };
                promise = centerClient.setTestMode(model);
                break;
            }
            case 'Imei': {
                const model: SetImeiModel = { DeviceId: deviceId, Imei: val };
                promise = centerClient.setImei(model);
                break;
            }
            case 'Comment': {
                const model: SetCommentModel = { DeviceIds: [deviceId], Comment: val };
                promise = centerClient.setComment(model);
                break;
            }
            case 'StoragePeriod': {
                const model: SetStoragePeriodModel = { DeviceIds: [deviceId], StoragePeriod: val };
                promise = centerClient.setStoragePeriod(model);
                break;
            }
        }
        if (promise)
            promise
                .then((res) => successSetProp(res.data))
                .catch(() => refreshTree());
    }

    const onSelectionChanged = (event: SelectionChangedEvent<Device>) => {
        const devices = event.selectedNodes?.map((node: IRowNode<Device>) => node.data!) || [];
        setSelectedDevices(devices)
    }

    const table = useRef<AgGridReact<Device>>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    // сброс скролла в топ только при смене выбранной ноды дерева;
    // при обновлении данных той же ноды позиция сохраняется (getRowId + suppressScrollOnNewData)
    const lastNodeKeyRef = useRef<string | null>(context.currentNode?.key ?? null);
    const pendingScrollResetRef = useRef(false);

    useEffect(() => {
        const key = context.currentNode?.key ?? null;
        if (key !== lastNodeKeyRef.current) {
            lastNodeKeyRef.current = key;
            pendingScrollResetRef.current = true;
        }
    }, [context.currentNode]);

    const onRowDataUpdated = () => {
        if (!pendingScrollResetRef.current)
            return;
        pendingScrollResetRef.current = false;
        table.current?.api.ensureIndexVisible(0, 'top');
    }

    function checkFoundDevices() {
        let devicesFound = false;
        try {
            if (!foundDeviceToShow) {
                return;
            }

            let rowId = 0;
            table.current?.api.forEachNodeAfterFilterAndSort(row => {
                const el = row.data!;
                const tableApi = table.current?.api;
                if (el.Model === foundDeviceToShow.deviceModel && el.Serial === foundDeviceToShow.deviceSerial) {
                    rowId = row.rowIndex!;
                    setFoundDeviceToShow(null);
                    pendingScrollResetRef.current = false;
                    tableApi?.setNodesSelected({ nodes: [row], newValue: true })
                    tableApi?.ensureIndexVisible(rowId, 'middle');
                    tableApi?.redrawRows({ rowNodes: [row] });
                    devicesFound = true;
                }
            });
        } catch (error) {
            console.error(error);
        }
        return devicesFound;
    }

    // Mobile wrappers — адаптируем существующие методы для вызова из MobileDevicesList
    const mobileSave = (deviceId: string, updated: Partial<Device>) => {
        // похожая логика на onMobileSave в предыдущей реализации
        Object.keys(updated).forEach((k) => {
            const key = k as keyof Device;
            const val = updated[key];
            if (val !== undefined && key !== 'Model' && key !== 'Serial' && key !== 'DeviceModelName') {
                handleChangeProperty(deviceId, key, val);
            }
        });
    };

    const mobileDelete = async (devices: Device[]) => {
        const deviceIds: string[] = [];
        let deviceNames: string = '';

        devices.map((deviceToRemove) => {
            const deviceId = buildDeviceId(deviceToRemove.Model, deviceToRemove.Serial);
            const deviceName = deviceToRemove.DeviceModelName + " " + deviceToRemove.Serial;
            deviceIds.push(deviceId);
            deviceNames += deviceName + "\r\n";
        })
        if (selectedDevices.length > 10)
            deviceNames = "Всего приборов - " + selectedDevices.length + ".";

        const confirmRemove = confirm({
            confirmation: `Вы действительно хотите удалить устройства? Устройства будут удалены вместе с данными.`,
            options: {
                body: deviceNames
            }
        });

        if (await confirmRemove) {
            removeDevices(deviceIds);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1.5rem', alignItems: 'center', padding:".25rem", flex: '0 0 auto' }} >
                { (!account || table.current == null) ? "" :
                    <>
                        <AccountInfo account={account} setAccount={setAccount} />
                        <ButtonGroup>
                            <Button title="Добавить устройство" variant="custom"
                                onClick={() =>
                                    setCreateModalOpen(true)
                                }
                            >
                                <AddCircleOutlineIcon style={{ fill: "var(--text-dark)" }} />
                            </Button>
                            <Button title="Удалить устройство" variant="custom"
                                onClick={() => handleRemoveDevices()}
                            >
                                <DeleteOutlineIcon style={{ fill: "var(--text-dark)" }} />
                            </Button>
                            <>
                                <SetDevicePropertyForm title="Заблокировать устройства" propertyName="Blocked" label="Блокировка" devices={selectedDevices} />
                                <SetDevicePropertyForm title="Отключить устройства" propertyName="Banned" label="Отключение" devices={selectedDevices} />
                                <SetDevicePropertyForm title="Установить цену дилера" propertyName="Cost" label="Цена дилера" devices={selectedDevices} />
                                <SetDevicePropertyForm title="Установить абонентскую плату в месяц" propertyName="Charge" label="Аб. плата в месяц" devices={selectedDevices} />
                                <SetDevicePropertyForm title="Установить тестовый режим" propertyName="TestMode" label="Тестовый режим" devices={selectedDevices} />
                                <SetDevicePropertyForm title="Установить баланс" propertyName="Balance" label="Баланс" devices={selectedDevices} />
                            </>
                        </ButtonGroup>
                        <div id="findDeviceContainer">
                            <FindDeviceInput />
                            <FindDeviceButton  />
                        </div>
                        <Button title={accountMessage?.Message} variant="custom" onClick={() => { setShowMessageDialog(true); }}>Сообщение</Button>
                    </>
                }
            </div>

            {isMobile ? (
                <>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <MobileDevicesList
                            account={account}
                            setAccount={setAccount}
                            data={data}
                            isLoading={isLoading}
                            onSave={mobileSave}
                            onDelete={mobileDelete}
                            setCreateModalOpen={setCreateModalOpen}
                            setShowMessageDialog={setShowMessageDialog}
                        />
                    </div>
                </>

            ) : (
                <div ref={tableContainerRef} style={{ height:'100%', maxHeight: 'clamp(250px, calc(100vh - 9rem), 9999px)' }}
                >
                    <BlickAgGrid
                        showRestoreColumnsButton={true}
                        rowData={uniqueDevices}
                        loading={isLoading}
                        ref={table}
                        //theme={myAgReactTheme}
                        localeText={AG_GRID_LOCALE_RU}
                        getRowStyle={getRowStyle}
                        columnDefs={columns}                        
                        suppressScrollOnNewData={true}
                        onRowDataUpdated={onRowDataUpdated}
                        getRowId={(params) => buildDeviceId(params.data.Model, params.data.Serial)}
                        onColumnResized={resizeHandle}          
                        onSelectionChanged={onSelectionChanged }
                        rowSelection={{
                            mode: "multiRow",
                            checkboxes: true,
                        }}
                        selectionColumnDef={{ maxWidth: 50 }}
                        onCellEditingStopped={(e: CellEditingStoppedEvent<Device>) => {
                            if (e.oldValue == e.newValue)
                                return;

                            const deviceId = buildDeviceId(e.node.data!.Model, e.node.data!.Serial);
                            const prop = e.colDef.field;
                            const value = e.value;
                            if (typeof e.value === 'number' && prop !== 'Balance' && e.newValue < 0) {
                                refreshTree();
                                return;
                            }                        
                            handleChangeProperty(deviceId, prop as keyof(Device), value);
                        }}
                        onModelUpdated={(e) => e.api.refreshCells()}
                    />
                </div>
            )}

            <AddDeviceForm
                tableColumns={columns}
                show={createModalOpen}
                account={account}
                onClose={() => setCreateModalOpen(false)}
            />
            <EditCommentDialog
                show={showCommentDialog}
                setShow={setShowCommentDialog}
                value={commentToEdit}
                setValue={setCommentToEdit }
            />
            <AccountMessageDialog
                login={account?.Login}
                show={showMessageDialog}
                data={accountMessage}
                setData={setAccountMessage}
                setShow={setShowMessageDialog}
            />
        </div>
    )
};

export default DevicesTable;
